import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CheckMark, OnboardingHeader } from "../../components/onboarding/OnboardingBrand";
import { TwMainLayout } from "../../components/tailwind";
import { classNames } from "../../helpers";
import * as userActions from "../../redux/reducers/userReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings, getUrlForUsers } from "../../utils/confighelpers";
import { getOnboardingRedirect, loadStripeJs, ONBOARDING_ROUTES } from "../../utils/onboarding";

/**
 * Onboarding step 2 in two screens:
 *   /start/plan                    (step="plan")      pick a plan, then "Continue"
 *   /start/checkout?plan=<planId>  (step="checkout")  Stripe Embedded Checkout inside our page + a summary of the plan
 * When there is exactly one monthly and one yearly plan they are shown side by side as "Monthly" / "Yearly" tiles.
 */

const BUSINESS_TYPE = "class";
const CHECKOUT_CONTAINER_ID = "fc-embedded-checkout";
export const SELECTED_PLAN_STORAGE_KEY = "fcOnboardingPlan";

const includedFeatures = [
  "Class schedule & online booking",
  "Credit passes & subscriptions",
  "Members' app with your timetable",
  "iDEAL, cards & Apple Pay",
  "News feed & push notifications",
  "Member management & stats",
];

const formatPrice = (price) => {
  const value = parseFloat(price || 0);
  return Number.isInteger(value) ? `€${value}` : `€${value.toFixed(2)}`;
};

const trialEndLabel = () => {
  const end = new Date();
  end.setDate(end.getDate() + 30);
  return end.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const perLabel = (plan) => (plan?.billingType === "year" ? "year" : "month");
const periodName = (plan) => (plan?.billingType === "year" ? "Yearly" : "Monthly");

const storedPlan = () => {
  try {
    return JSON.parse(localStorage.getItem(SELECTED_PLAN_STORAGE_KEY) || "null");
  } catch (e) {
    return null;
  }
};

function IncludedCard() {
  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-5 sm:p-6">
      <h2 className="text-sm font-extrabold text-gray-800">Included in every plan</h2>
      <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm font-semibold text-gray-700 sm:grid-cols-2 lg:grid-cols-3">
        {includedFeatures.map((feature) => (
          <li key={feature} className="flex items-center gap-2"><CheckMark className="h-4 w-4 shrink-0 text-bz-status-green" />{feature}</li>
        ))}
      </ul>
    </div>
  );
}

function FeatureList({ features }) {
  if (!Array.isArray(features) || features.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-1.5 text-sm text-gray-600">
      {features.map((feature) => (
        <li key={feature} className="flex gap-2">
          <CheckMark className="mt-0.5 h-4 w-4 shrink-0 text-bz-status-green" />
          <span className="flex-1" dangerouslySetInnerHTML={{ __html: feature }} />
        </li>
      ))}
    </ul>
  );
}

const ArrowRight = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const ArrowLeft = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

export default function PlanCheckout({ navigation, step = "plan" }) {
  const isCheckout = step === "checkout";
  const dispatch = useDispatch();
  const location = useLocation();
  const userReduxState = useSelector(userState);
  const [plans, setPlans] = React.useState([]);
  const [frequency, setFrequency] = React.useState("month");
  const [selectedPlanId, setSelectedPlanId] = React.useState("");
  const [checkoutStatus, setCheckoutStatus] = React.useState("idle"); // idle | loading | ready | error
  const [errorMessage, setErrorMessage] = React.useState("");
  const [pageLoading, setPageLoading] = React.useState(true);
  const checkoutRef = React.useRef(null);
  const requestRef = React.useRef(0);

  const onBoardingData = userReduxState?.onBoardingData || {};
  const clubName = location.state?.clubName || onBoardingData.clubName || "";
  const planFromUrl = new URLSearchParams(location.search).get("plan") || "";

  const destroyCheckout = () => {
    if (checkoutRef.current) {
      try {
        checkoutRef.current.destroy();
      } catch (e) {
        // already destroyed
      }
      checkoutRef.current = null;
    }
  };

  const mountCheckout = async (plan) => {
    const requestId = ++requestRef.current;
    destroyCheckout();
    setErrorMessage("");
    setCheckoutStatus("loading");
    try {
      const response = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}payments/createStripeSubscriptionCheckoutSessions`,
        data: {
          bookzySubscriptionId: plan._id,
          uiMode: "embedded",
          return_url: `${window.location.origin}${ONBOARDING_ROUTES.welcome}`,
        },
      });
      const data = response?.data?.data;
      if (response?.data?.statusCode !== 200 || !data?.client_secret) {
        throw new Error(response?.data?.message || "We couldn't open the payment form.");
      }
      const stripe = await loadStripeJs();
      const checkout = await stripe.initEmbeddedCheckout({ fetchClientSecret: async () => data.client_secret });
      if (requestId !== requestRef.current) {
        checkout.destroy();
        return;
      }
      checkoutRef.current = checkout;
      checkout.mount(`#${CHECKOUT_CONTAINER_ID}`);
      setCheckoutStatus("ready");
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setErrorMessage(error?.message || "We couldn't open the payment form.");
      setCheckoutStatus("error");
    }
  };

  const rememberPlan = (plan) => {
    try {
      localStorage.setItem(SELECTED_PLAN_STORAGE_KEY, JSON.stringify({ _id: plan._id, title: plan.title, price: plan.price, billingType: plan.billingType }));
    } catch (e) {
      // storage unavailable: welcome screen falls back to generic copy
    }
  };

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const userResponse = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getUserInfo` });
        if (userResponse?.data?.statusCode === 200) {
          const userData = userResponse.data.data;
          dispatch(userActions.setUserData(userData));
          if (!userReduxState.businessType || userReduxState.businessType === "NA" || userReduxState.businessType === "default") {
            dispatch(userActions.setBusinessType(BUSINESS_TYPE));
          }
          const next = getOnboardingRedirect(userData);
          if (next !== ONBOARDING_ROUTES.plan) {
            navigation(next || "/home/dashboard", { replace: true });
            return;
          }
        }
        const planResponse = await axiosInstance({ method: "get", url: `${getUrlForUsers()}getSubscriptionPlans?listFor=${BUSINESS_TYPE}` });
        if (!alive) return;
        const activePlans = (planResponse?.data?.data || []).filter(
          (plan) => plan.isActive === true && plan?.stripeSubscriptionDetails?.stripeSubscriptionPriceId
        );
        setPlans(activePlans);

        if (isCheckout) {
          const plan = activePlans.find((p) => p._id === planFromUrl);
          if (!plan) {
            navigation(ONBOARDING_ROUTES.plan, { replace: true });
            return;
          }
          setSelectedPlanId(plan._id);
          rememberPlan(plan);
          mountCheckout(plan);
        } else {
          // Keep the plan chosen before (coming back from checkout), otherwise the first monthly plan.
          const previous = planFromUrl || storedPlan()?._id;
          const start = activePlans.find((p) => p._id === previous) || activePlans.find((p) => p.billingType === "month") || activePlans[0];
          if (start) {
            setFrequency(start.billingType);
            setSelectedPlanId(start._id);
          }
        }
      } finally {
        if (alive) setPageLoading(false);
      }
    })();
    return () => {
      alive = false;
      destroyCheckout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckout, planFromUrl]);

  const hasYearly = plans.some((plan) => plan.billingType === "year");
  const hasMonthly = plans.some((plan) => plan.billingType === "month");
  const monthlyPlans = plans.filter((plan) => plan.billingType === "month");
  const yearlyPlans = plans.filter((plan) => plan.billingType === "year");
  // One plan, billed monthly or yearly: show both as two tiles side by side (no Monthly/Yearly switch).
  const sideBySide = monthlyPlans.length === 1 && yearlyPlans.length === 1;
  const visiblePlans = sideBySide ? [monthlyPlans[0], yearlyPlans[0]] : plans.filter((plan) => plan.billingType === frequency);
  const selectedPlan = plans.find((plan) => plan._id === selectedPlanId);
  /** Yearly vs 12 × monthly (same plan family): "Save €60 (17%)" */
  const yearlySaving = (() => {
    if (!sideBySide) return null;
    const perYearMonthly = parseFloat(monthlyPlans[0].price || 0) * 12;
    const yearly = parseFloat(yearlyPlans[0].price || 0);
    if (!(perYearMonthly > 0) || !(yearly > 0) || yearly >= perYearMonthly) return null;
    const amount = perYearMonthly - yearly;
    return { amount, pct: Math.round((amount / perYearMonthly) * 100) };
  })();

  const changeFrequency = (value) => {
    setFrequency(value);
    const first = plans.find((plan) => plan.billingType === value);
    if (first) setSelectedPlanId(first._id);
  };

  const goToCheckout = () => {
    if (!selectedPlan) return;
    rememberPlan(selectedPlan);
    navigation(`${ONBOARDING_ROUTES.checkout}?plan=${encodeURIComponent(selectedPlan._id)}`, { state: location.state });
  };

  const backToPlans = () => navigation(`${ONBOARDING_ROUTES.plan}?plan=${encodeURIComponent(selectedPlanId || planFromUrl)}`, { state: location.state });

  const planName = (plan) => `${plan.title} · ${periodName(plan)}`;

  /* ---------------------------------------------------------------- step 2b: checkout */
  if (isCheckout) {
    return (
      <TwMainLayout fullScreen={true}>
        <div className="min-h-screen bg-[#F7F4F8]">
          <OnboardingHeader current={2} />
          <main className="mx-auto w-full max-w-6xl px-4 pb-12 pt-5 sm:px-8 sm:pt-7">
            <button type="button" onClick={backToPlans} className="inline-flex items-center gap-1.5 rounded-lg py-1 text-sm font-bold text-gray-600 hover:text-gray-900">
              <ArrowLeft />
              Back to plans
            </button>

            <div className="mt-3 flex flex-col gap-5 lg:mt-4 lg:flex-row lg:items-start lg:gap-8">
              {/* Summary of the chosen plan */}
              <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-[360px]">
                {pageLoading || !selectedPlan ? (
                  <div className="h-24 animate-pulse rounded-3xl bg-white lg:h-80" />
                ) : (
                  <div className="rounded-3xl border border-gray-200 bg-white p-4 sm:p-6">
                    <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-gray-500">Your plan</p>
                    <div className="mt-1.5 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl font-black text-gray-900">{planName(selectedPlan)}</h1>
                        <p className="mt-0.5 text-sm text-gray-600">
                          {formatPrice(selectedPlan.price)}/{perLabel(selectedPlan)}
                          {selectedPlan.billingType === "year" && ` · ${formatPrice(parseFloat(selectedPlan.price || 0) / 12)}/month`}
                        </p>
                      </div>
                      <button type="button" onClick={backToPlans} className="shrink-0 rounded-lg px-1 text-sm font-extrabold text-indigo-bz underline-offset-2 hover:underline">
                        Change
                      </button>
                    </div>

                    <dl className="mt-4 flex flex-col gap-2 rounded-2xl bg-[#F7F4F8] p-3.5 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <dt className="font-bold text-gray-900">Due today</dt>
                        <dd className="text-lg font-black text-[#1F6B3A]">€0</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-gray-600">From {trialEndLabel()}</dt>
                        <dd className="font-bold text-gray-900">{formatPrice(selectedPlan.price)}/{perLabel(selectedPlan)}</dd>
                      </div>
                    </dl>

                    <div className="hidden lg:block">
                      {selectedPlan.description && (
                        <p className="mt-4 rounded-xl bg-[#F3E9FF] px-3 py-2 text-sm font-bold text-[#4B0084]">{selectedPlan.description}</p>
                      )}
                      <div className="mt-3">
                        <FeatureList features={selectedPlan.subscriptionDetail} />
                      </div>
                      <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
                        Cancel before {trialEndLabel()} and you pay nothing. Upgrade or downgrade anytime from your account.
                      </p>
                    </div>
                  </div>
                )}
              </aside>

              {/* Stripe Embedded Checkout */}
              <section className="min-w-0 flex-1">
                <div className="rounded-3xl border border-gray-200 bg-white p-4 sm:p-6 shadow-[0_20px_50px_rgba(72,27,58,0.10)]">
                  <h2 className="text-lg sm:text-xl font-black text-gray-900">Start your free trial</h2>
                  <p className="mt-1 text-sm text-gray-600">You won't be charged today.</p>
                  <div className={classNames("relative mt-4", checkoutStatus === "error" ? "" : "min-h-[420px]")}>
                    {(checkoutStatus === "loading" || checkoutStatus === "idle") && (
                      <div className="absolute inset-0 flex flex-col gap-3" aria-label="Loading payment form">
                        <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
                        <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
                        <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
                        <div className="h-12 w-2/3 animate-pulse rounded-xl bg-gray-100" />
                      </div>
                    )}
                    {checkoutStatus === "error" && (
                      <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-800" role="alert">
                        <p className="font-bold">The payment form didn't load.</p>
                        <p className="mt-1">{errorMessage}</p>
                        {selectedPlan && (
                          <button type="button" className="mt-3 font-extrabold text-indigo-bz underline" onClick={() => mountCheckout(selectedPlan)}>
                            Try again
                          </button>
                        )}
                      </div>
                    )}
                    <div id={CHECKOUT_CONTAINER_ID} className={checkoutStatus === "ready" ? "" : "invisible h-0 overflow-hidden"} />
                  </div>
                  <p className="mt-4 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-gray-500">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                    Secured by Stripe · cancel anytime
                  </p>
                </div>
              </section>
            </div>
          </main>
        </div>
      </TwMainLayout>
    );
  }

  /* ---------------------------------------------------------------- step 2a: pick a plan */
  return (
    <TwMainLayout fullScreen={true}>
      <div className="min-h-screen bg-[#F7F4F8]">
        <OnboardingHeader current={2} />

        <div className="mx-auto w-full max-w-5xl px-4 sm:px-8">
          <div className="relative mt-5 flex items-center gap-3 overflow-hidden rounded-2xl bg-brown-coffee px-4 py-3.5 sm:px-6 text-white">
            <span className="fc-shine pointer-events-none absolute inset-y-0 left-0 w-32 bg-white/10" aria-hidden="true" />
            <span className="fc-bob flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-portland-orange" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><rect x="3" y="8" width="18" height="13" rx="2" /><path d="M12 8v13M3 12h18M7.5 8a2.5 2.5 0 1 1 4.5-2 2.5 2.5 0 1 1 4.5 2" /></svg>
            </span>
            <p className="text-sm sm:text-base">
              <span className="font-extrabold">Your first 30 days are on us.</span>{" "}
              <span className="text-[#E9DDE6]">You won't be charged today — cancel before {trialEndLabel()} and you pay nothing.</span>
            </p>
          </div>
        </div>

        <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pb-12 pt-6 sm:px-8">
          <section className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">
              Pick the plan that fits {clubName || "your club"}
            </h1>
            <p className="mt-1.5 text-sm sm:text-base text-gray-600">Upgrade or downgrade anytime from your account.</p>

            {hasMonthly && hasYearly && !sideBySide && (
              <div className="mt-5 inline-flex rounded-full bg-[#EDE7F0] p-1" role="group" aria-label="Billing period">
                {[{ value: "month", label: "Monthly" }, { value: "year", label: "Yearly" }].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => changeFrequency(option.value)}
                    aria-pressed={frequency === option.value}
                    className={classNames(
                      "h-9 rounded-full px-5 text-sm font-bold transition",
                      frequency === option.value ? "bg-white text-gray-900 shadow" : "text-gray-600 hover:text-gray-900"
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {pageLoading ? (
              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                {[0, 1].map((i) => <div key={i} className="h-56 animate-pulse rounded-3xl bg-white" />)}
              </div>
            ) : visiblePlans.length === 0 ? (
              <p className="mt-6 rounded-2xl bg-white p-6 text-gray-700">No plans are available right now. Please contact us and we'll set you up.</p>
            ) : (
              <div className={classNames("mt-6 grid grid-cols-1 gap-4", visiblePlans.length >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2")}>
                {visiblePlans.map((plan) => {
                  const selected = plan._id === selectedPlanId;
                  const isYearly = plan.billingType === "year";
                  return (
                    <button
                      key={plan._id}
                      type="button"
                      onClick={() => setSelectedPlanId(plan._id)}
                      aria-pressed={selected}
                      className={classNames(
                        "flex flex-col gap-3 rounded-3xl bg-white p-5 sm:p-6 text-left transition",
                        selected ? "border-[2.5px] border-indigo-bz shadow-[0_16px_36px_rgba(126,0,221,0.16)]" : "border-[1.5px] border-gray-200 hover:border-indigo-bz/50"
                      )}
                    >
                      <span className="flex items-center justify-between">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className={classNames("text-base font-extrabold", selected ? "text-indigo-bz" : "text-gray-700")}>
                            {sideBySide ? periodName(plan) : plan.title}
                          </span>
                          {sideBySide && isYearly && yearlySaving && (
                            <span className="rounded-full bg-[#E8F7EE] px-2 py-0.5 text-xs font-extrabold text-[#1F6B3A]">
                              Save {formatPrice(yearlySaving.amount)} ({yearlySaving.pct}%)
                            </span>
                          )}
                        </span>
                        <span className={classNames("flex h-6 w-6 items-center justify-center rounded-full", selected ? "bg-indigo-bz text-white" : "border-2 border-gray-300")}>
                          {selected && <CheckMark className="h-3.5 w-3.5" />}
                        </span>
                      </span>
                      <span className="flex items-baseline gap-1.5">
                        <span className="text-3xl sm:text-4xl font-black text-gray-900">{formatPrice(plan.price)}</span>
                        <span className="text-sm text-gray-500">/{perLabel(plan)}</span>
                      </span>
                      {sideBySide && (
                        <span className="-mt-1.5 text-sm text-gray-500">
                          {isYearly ? `${plan.title} · ${formatPrice(parseFloat(plan.price || 0) / 12)}/month, billed once a year` : `${plan.title} · billed every month, cancel anytime`}
                        </span>
                      )}
                      {plan.description && (
                        <span className={classNames("rounded-xl px-3 py-2 text-sm font-bold", selected ? "bg-[#F3E9FF] text-[#4B0084]" : "bg-[#F7F4F8] text-gray-800")}>
                          {plan.description}
                        </span>
                      )}
                      <FeatureList features={plan.subscriptionDetail} />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Continue: right under the tiles; on phones it stays pinned to the bottom while scrolling */}
            {!pageLoading && selectedPlan && (
              <div className="sticky bottom-0 z-20 -mx-4 mt-5 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:rounded-3xl sm:border sm:px-5 sm:py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-extrabold text-gray-900">{planName(selectedPlan)}</p>
                    <p className="truncate text-xs sm:text-sm text-gray-600">
                      €0 today · then {formatPrice(selectedPlan.price)}/{perLabel(selectedPlan)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={goToCheckout}
                    className="inline-flex h-12 shrink-0 items-center gap-2 rounded-2xl bg-indigo-bz px-5 sm:px-7 text-sm sm:text-base font-extrabold text-white shadow-[0_10px_24px_rgba(126,0,221,0.25)] transition hover:bg-[#6A00BC] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#D9C2F2]"
                  >
                    Continue
                    <ArrowRight />
                  </button>
                </div>
              </div>
            )}
          </section>

          <IncludedCard />
        </main>
      </div>
    </TwMainLayout>
  );
}
